import { FC, useEffect } from "react";
import { useRouter } from "next/router";
import Dialog, {
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@/components/Dialog";
import Button from "@/components/Button";
import Typography from "@/components/Typography";
import styles from "./JobMatchVideoDialog.module.scss";
import { useTrackers, useCurrentTrip } from "@/hooks";
import OrientationVideoPlayer from "./OrientationVideoPlayer";
import { useDetailsTripQuery } from "@/fetch/gworld";
import { useMarkTaskCompleteMutation, useTripTasksQuery } from "@/fetch/tasks";

type Props = {
  onClose: () => void;
};

const JobMatchVideoDialog: FC<Props> = ({ onClose }) => {
  const { currentTrip } = useCurrentTrip();
  const { data: tripDetails, isLoading: isLoadingTripDetails } =
    useDetailsTripQuery(currentTrip?.id, {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    });

  const { mutateAsync: markTaskComplete, isLoading: isCompletingTask } =
    useMarkTaskCompleteMutation();
  const { data: tasksData, isLoading: isLoadingTasks } = useTripTasksQuery();
  const { track, captureException } = useTrackers();

  const jobMatchVideoTask = tasksData?.result?.find(
    (eachTask) => eachTask.task_id === tripDetails?.job_match_video_task_id
  );

  useEffect(() => {
    if (Boolean(jobMatchVideoTask?.complete)) {
      onClose();
    }
  }, [jobMatchVideoTask]);

  useEffect(() => {
    if (!tripDetails) return;
    if (!Boolean(tripDetails?.job_match_video_signed_url?.["video/original"])) {
      onClose();
    }
  }, [tripDetails]);

  const updateJobMatchTask = async () => {
    const jobMatchVideoTaskId = tripDetails?.job_match_video_task_id;
    track("Job Match Video Watched", {
      eventId: "job-match-video-watched",
    });
    if (!jobMatchVideoTaskId || Boolean(jobMatchVideoTask?.complete)) {
      onClose();
      return;
    }
    if (isCompletingTask || isLoadingTasks) return;
    try {
      await markTaskComplete({ taskId: jobMatchVideoTaskId });
      onClose();
    } catch (error) {
      console.error({ error });
      captureException(error);
    }
  };

  const updateVideoProgress = (progress: number) => {
    track("Job Match Video Progress Updated", {
      eventId: "job-match-video-progress-updated",
      progress: progress,
    });
  };

  return (
    <Dialog open>
      <DialogContent className={styles.dialogContent}>
        <Typography variant="h6">Job Match Process</Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          className={styles.description}
        >
          To understand how our job matching process works, please watch this
          video before your Arrival Coordinator reaches out! Once the video is
          finished, you will then be able to navigate gWorld as normal. You can
          refer back to this video at anytime in the Arrival section.
        </Typography>
        <div className={styles.videoContainer}>
          {tripDetails?.job_match_video_signed_url && (
            <OrientationVideoPlayer
              orientationVideoUrls={tripDetails?.job_match_video_signed_url}
              onVideoWatched={() => {}}
              onProgressUpdate={updateVideoProgress}
              onVideoCompleted={updateJobMatchTask}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobMatchVideoDialog;
